import React from 'react';
import useEditorStore from '../../stores/useEditorStore';
import { copyCustomProps } from '../../utils/clipboardUtils';
import { extractImageFromFrame } from '../../utils/frameUtils';

/**
 * 캔버스 범용 우클릭 컨텍스트 메뉴
 * @param {{ contextMenu, setContextMenu, canvas }} props
 * contextMenu: { x, y, target, activeObjects }
 */
const CanvasContextMenu = ({ contextMenu, setContextMenu, canvas }) => {
  if (!contextMenu || !canvas) return null;

  const { activeObjects } = contextMenu;
  const store = useEditorStore.getState();
  const { pushHistory, syncLayersFromCanvas, groupObjects, ungroupObjects } = store;

  const hasSelection = activeObjects.length > 0;
  const isMultiSelect = activeObjects.length >= 2;
  const isSingleGroup = activeObjects.length === 1
    && activeObjects[0].type === 'group'
    && !activeObjects[0]._isTextBoxGroup
    && !activeObjects[0]._isTableGroup;

  const close = () => setContextMenu(null);

  // ─── 기본 액션 ───

  const handleCopy = async () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        const cloned = await activeObject.clone();
        copyCustomProps(activeObject, cloned);
        window._clipboard = cloned;
        window._clipboardSource = activeObject;
      } catch (err) { console.error('Copy error:', err); }
    }
    close();
  };

  const handlePaste = async () => {
    if (window._clipboard) {
      try {
        const cloned = await window._clipboard.clone();
        copyCustomProps(window._clipboard, cloned);
        cloned.set({ left: cloned.left + 20, top: cloned.top + 20, id: `${cloned.type}-${Date.now()}` });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        canvas.renderAll();
        pushHistory();
      } catch (err) { console.error('Paste error:', err); }
    }
    close();
  };

  const handleDuplicate = async () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        const cloned = await activeObject.clone();
        copyCustomProps(activeObject, cloned);
        cloned.set({ left: cloned.left + 20, top: cloned.top + 20, id: `${cloned.type}-${Date.now()}` });
        canvas.add(cloned);
        canvas.setActiveObject(cloned);
        canvas.renderAll();
        pushHistory();
      } catch (err) { console.error('Duplicate error:', err); }
    }
    close();
  };

  const handleDelete = () => {
    const objs = canvas.getActiveObjects();
    objs.forEach(obj => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.renderAll();
    pushHistory();
    close();
  };

  // ─── Z-Order ───

  const handleBringToFront = () => {
    const obj = canvas.getActiveObject();
    if (obj) { canvas.bringObjectToFront(obj); canvas.renderAll(); pushHistory(); syncLayersFromCanvas(); }
    close();
  };

  const handleSendToBack = () => {
    const obj = canvas.getActiveObject();
    if (obj) { canvas.sendObjectToBack(obj); canvas.renderAll(); pushHistory(); syncLayersFromCanvas(); }
    close();
  };

  const handleBringForward = () => {
    const obj = canvas.getActiveObject();
    if (obj) { canvas.bringObjectForward(obj); canvas.renderAll(); pushHistory(); syncLayersFromCanvas(); }
    close();
  };

  const handleSendBackward = () => {
    const obj = canvas.getActiveObject();
    if (obj) { canvas.sendObjectBackwards(obj); canvas.renderAll(); pushHistory(); syncLayersFromCanvas(); }
    close();
  };

  // ─── 잠금 ───

  const isLocked = hasSelection && activeObjects.every(obj => obj.selectable === false);

  const handleToggleLock = () => {
    activeObjects.forEach(obj => {
      const locked = obj.selectable === false;
      obj.set({ selectable: locked, evented: locked });
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    syncLayersFromCanvas();
    close();
  };

  // ─── 그룹화 / 해제 ───

  const handleGroup = () => {
    groupObjects();
    close();
  };

  const handleUngroup = () => {
    ungroupObjects();
    close();
  };

  // ─── 정렬 (다중 선택) ───

  const getAlignBounds = () => {
    let minLeft = Infinity, minTop = Infinity, maxRight = -Infinity, maxBottom = -Infinity;
    activeObjects.forEach(obj => {
      const bound = obj.getBoundingRect();
      minLeft = Math.min(minLeft, bound.left);
      minTop = Math.min(minTop, bound.top);
      maxRight = Math.max(maxRight, bound.left + bound.width);
      maxBottom = Math.max(maxBottom, bound.top + bound.height);
    });
    return { minLeft, minTop, maxRight, maxBottom, centerX: (minLeft + maxRight) / 2, centerY: (minTop + maxBottom) / 2 };
  };

  const alignObjects = (direction) => {
    const bounds = getAlignBounds();
    activeObjects.forEach(obj => {
      const objBound = obj.getBoundingRect();
      switch (direction) {
        case 'left':
          obj.set('left', obj.left + (bounds.minLeft - objBound.left));
          break;
        case 'centerH':
          obj.set('left', obj.left + (bounds.centerX - (objBound.left + objBound.width / 2)));
          break;
        case 'right':
          obj.set('left', obj.left + (bounds.maxRight - (objBound.left + objBound.width)));
          break;
        case 'top':
          obj.set('top', obj.top + (bounds.minTop - objBound.top));
          break;
        case 'centerV':
          obj.set('top', obj.top + (bounds.centerY - (objBound.top + objBound.height / 2)));
          break;
        case 'bottom':
          obj.set('top', obj.top + (bounds.maxBottom - (objBound.top + objBound.height)));
          break;
        default: break;
      }
      obj.setCoords();
    });
    canvas.renderAll();
    pushHistory();
    close();
  };

  // ─── 메뉴 구성 ───

  const menuItems = [];

  // 기본 메뉴 (선택 있을 때)
  if (hasSelection) {
    menuItems.push({ label: '복사', shortcut: 'Ctrl+C', action: handleCopy });
    menuItems.push({ label: '붙여넣기', shortcut: 'Ctrl+V', action: handlePaste });
    menuItems.push({ label: '복제', shortcut: 'Ctrl+D', action: handleDuplicate });
    menuItems.push({ label: '삭제', shortcut: 'Delete', action: handleDelete, danger: true });
    menuItems.push({ type: 'divider' });

    // Z-Order (단일 선택)
    if (!isMultiSelect) {
      menuItems.push({ label: '맨 앞으로', action: handleBringToFront });
      menuItems.push({ label: '앞으로', action: handleBringForward });
      menuItems.push({ label: '뒤로', action: handleSendBackward });
      menuItems.push({ label: '맨 뒤로', action: handleSendToBack });
      menuItems.push({ type: 'divider' });
    }

    // 잠금
    menuItems.push({ label: isLocked ? '잠금 해제' : '잠금', action: handleToggleLock });

    // 프레임 메뉴
    if (activeObjects.length === 1 && activeObjects[0]._isFrame) {
      const frameObj = activeObjects[0];
      menuItems.push({ type: 'divider' });
      menuItems.push({
        label: frameObj._frameLocked ? '프레임 잠금 해제' : '프레임 잠금',
        action: () => {
          frameObj._frameLocked = !frameObj._frameLocked;
          canvas.renderAll();
          close();
        },
      });
      if (!frameObj._isEmptyFrame) {
        menuItems.push({
          label: '사진 빼내기',
          action: () => {
            extractImageFromFrame(frameObj, canvas);
            syncLayersFromCanvas();
            pushHistory();
            close();
          },
        });
      }
    }

    // 그룹화 (다중 선택)
    if (isMultiSelect) {
      menuItems.push({ type: 'divider' });
      menuItems.push({ label: '그룹화', shortcut: 'Ctrl+G', action: handleGroup });
    }

    // 그룹 해제 (단일 그룹)
    if (isSingleGroup) {
      menuItems.push({ type: 'divider' });
      menuItems.push({ label: '그룹 해제', shortcut: 'Ctrl+Shift+G', action: handleUngroup });
    }

    // 정렬 (다중 선택)
    if (isMultiSelect) {
      menuItems.push({ type: 'divider' });
      menuItems.push({ label: '좌측 정렬', action: () => alignObjects('left') });
      menuItems.push({ label: '가운데 정렬', action: () => alignObjects('centerH') });
      menuItems.push({ label: '우측 정렬', action: () => alignObjects('right') });
      menuItems.push({ type: 'divider' });
      menuItems.push({ label: '상단 정렬', action: () => alignObjects('top') });
      menuItems.push({ label: '중간 정렬', action: () => alignObjects('centerV') });
      menuItems.push({ label: '하단 정렬', action: () => alignObjects('bottom') });
    }
  } else {
    // 선택 없을 때: 붙여넣기만
    menuItems.push({ label: '붙여넣기', shortcut: 'Ctrl+V', action: handlePaste, disabled: !window._clipboard });
  }

  return (
    <div
      style={{
        position: 'absolute', left: contextMenu.x, top: contextMenu.y, zIndex: 200,
        backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)', padding: '4px 0', minWidth: '180px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {menuItems.map((item, idx) => {
        if (item.type === 'divider') {
          return <div key={idx} style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '4px 0' }} />;
        }
        return (
          <button
            key={idx}
            onClick={item.action}
            disabled={item.disabled}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '7px 16px', fontSize: '13px',
              textAlign: 'left', border: 'none', backgroundColor: 'transparent',
              cursor: item.disabled ? 'default' : 'pointer',
              color: item.disabled ? '#9ca3af' : item.danger ? '#dc2626' : '#1f2937',
              opacity: item.disabled ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) e.currentTarget.style.backgroundColor = item.danger ? '#fef2f2' : '#eff6ff';
            }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span>{item.label}</span>
            {item.shortcut && (
              <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '24px' }}>{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default CanvasContextMenu;
